import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Layout } from '../components/layout/Layout'
import { publicApiClient, adminApiClient, handleApiError } from '../api/client'
import type { Image } from '../types/image'
import type { Album } from '../types/album'
import { getImageUrl, getThumbnailUrl } from '../lib/images'
import {
  ArrowLeft,
  Trash2,
  Edit3,
  Calendar,
  Camera,
  Aperture,
  Timer,
  Gauge,
  Ruler,
  Hash,
  Tag,
  Folder,
  ExternalLink,
  X,
  Check,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { VisibilityBadge } from '../components/common/VisibilityBadge'
import { useDeleteImage, useUpdateImage } from '../hooks/useImages'
import { useAlbums } from '../hooks/useAlbums'
import { formatFileSize, formatDate } from '../utils/formatting'
import { Loading } from '../components/common/Loading'
import { DownloadOptions } from '../components/common/DownloadOptions'

export const ImageDetail = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore()
  const isAdmin = isAuthenticated && user?.isAdmin
  const [isZoomed, setIsZoomed] = useState(false)
  const [zoomImageLoading, setZoomImageLoading] = useState(false)
  const [isEditingAlbum, setIsEditingAlbum] = useState(false)
  const [selectedAlbumId, setSelectedAlbumId] = useState('')
  const [albumError, setAlbumError] = useState<string | null>(null)
  const deleteMutation = useDeleteImage(adminApiClient)
  const updateImage = useUpdateImage(adminApiClient)
  const { data: albums } = useAlbums(adminApiClient)

  const { data: image, isLoading } = useQuery({
    queryKey: ['image', id],
    queryFn: async () => {
      const response = await publicApiClient.get(`/images/${id}`)
      return response.data.data as Image
    },
    enabled: !!id,
  })

  // Sync the selected album when the image loads.
  useEffect(() => {
    if (image) {
      setSelectedAlbumId(image.albumId)
    }
  }, [image])

  // A photo inherits its reachability from its album; show the album's
  // visibility badge rather than a (now-nonexistent) per-image one.
  const { data: album } = useQuery({
    queryKey: ['album', 'public', image?.albumId],
    queryFn: async () => {
      const response = await publicApiClient.get(`/albums/${image!.albumId}`)
      return response.data.data as Album
    },
    enabled: !!image?.albumId,
  })

  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto animate-pulse">
          <div className="aspect-[16/9] bg-gray-200 rounded-2xl mb-6" />
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
        </div>
      </Layout>
    )
  }

  if (!image) {
    return (
      <Layout>
        <div className="text-center py-20">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Image not found</h1>
          <p className="text-gray-500 mb-6">This image doesn't exist or has been deleted</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Gallery
          </Link>
        </div>
      </Layout>
    )
  }

  const imageUrl = getThumbnailUrl(image, 'large')
  const originalUrl = getImageUrl(image.originalKey, image.updatedAt)

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          {isAuthenticated && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/admin')}
                aria-label="Admin dashboard"
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Edit3 className="w-4 h-4" />
                Admin
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this image?')) {
                    deleteMutation.mutate(id!, {
                      onSuccess: () => navigate(-1),
                    })
                  }
                }}
                disabled={deleteMutation.isPending}
                aria-label="Delete image"
                className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Image */}
          <div className="lg:col-span-2">
            <div 
              className="relative bg-gray-100 rounded-2xl overflow-hidden shadow-lg cursor-zoom-in group"
              onClick={() => setIsZoomed(true)}
            >
              <img
                src={imageUrl}
                alt={image.filename}
                className="w-full h-auto max-h-[70vh] object-contain"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
              <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="bg-black/50 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm">
                  Click to zoom
                </span>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="flex items-center gap-3 mt-4">
              <DownloadOptions image={image} defaultSize="original" variant="button" />
              <a
                href={originalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                View Full Size
              </a>
            </div>
          </div>

          {/* Details Sidebar */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h1 className="text-xl font-bold text-gray-900 mb-2">{image.filename}</h1>
              
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {album && <VisibilityBadge visibility={album.visibility} />}
                <span className="text-xs text-gray-400">
                  {image.contentType.split('/')[1].toUpperCase()}
                </span>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3 text-gray-600">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>Uploaded {formatDate(image.uploadDate)}</span>
                </div>
                
                <div className="flex items-center gap-3 text-gray-600">
                  <Folder className="w-4 h-4 text-gray-400" />
                  {isEditingAlbum ? (
                    <div className="flex-1 space-y-2">
                      <select
                        value={selectedAlbumId}
                        onChange={(e) => setSelectedAlbumId(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                        disabled={updateImage.isPending}
                      >
                        {albums?.map((album) => (
                          <option key={album.albumId} value={album.albumId}>
                            {album.name}
                          </option>
                        ))}
                      </select>
                      {albumError && (
                        <p className="text-xs text-red-600" role="alert">
                          {albumError}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            setAlbumError(null)
                            if (!selectedAlbumId || selectedAlbumId === image.albumId) {
                              setIsEditingAlbum(false)
                              return
                            }
                            try {
                              await updateImage.mutateAsync({
                                id: image.imageId,
                                data: { albumId: selectedAlbumId },
                              })
                              setIsEditingAlbum(false)
                            } catch (error) {
                              setAlbumError(handleApiError(error as Error))
                            }
                          }}
                          className="flex items-center gap-1 px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
                          disabled={updateImage.isPending || !selectedAlbumId}
                        >
                          {updateImage.isPending ? (
                            'Saving...'
                          ) : (
                            <>
                              <Check className="w-3 h-3" />
                              Save
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setSelectedAlbumId(image.albumId)
                            setAlbumError(null)
                            setIsEditingAlbum(false)
                          }}
                          className="flex items-center gap-1 px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded text-xs"
                          disabled={updateImage.isPending}
                        >
                          <X className="w-3 h-3" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between flex-1">
                      <Link to={`/album/${image.albumId}`} className="text-blue-600 hover:underline">
                        {album?.name || 'View Album'}
                      </Link>
                      {isAdmin && (
                        <button
                          onClick={() => setIsEditingAlbum(true)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Move
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 text-gray-600">
                  <Gauge className="w-4 h-4 text-gray-400" />
                  <span>{formatFileSize(image.fileSize)}</span>
                </div>

                <div className="flex items-center gap-3 text-gray-600">
                  <Ruler className="w-4 h-4 text-gray-400" />
                  <span>{image.width} × {image.height} px</span>
                </div>
              </div>
            </div>

            {/* EXIF Data */}
            {image.exifData && Object.keys(image.exifData).length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                  Camera Information
                </h3>
                
                <div className="space-y-3">
                  {image.exifData.camera && (
                    <div className="flex items-start gap-3">
                      <Camera className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-400">Camera</p>
                        <p className="text-sm font-medium text-gray-900">{image.exifData.camera}</p>
                      </div>
                    </div>
                  )}
                  
                  {image.exifData.lens && (
                    <div className="flex items-start gap-3">
                      <div className="w-4 h-4 rounded-full border-2 border-gray-300 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-400">Lens</p>
                        <p className="text-sm font-medium text-gray-900">{image.exifData.lens}</p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-4 pt-3 border-t border-gray-100">
                    {image.exifData.aperture && (
                      <div className="text-center">
                        <Aperture className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                        <p className="text-xs text-gray-400">Aperture</p>
                        <p className="text-sm font-semibold text-gray-900">{image.exifData.aperture}</p>
                      </div>
                    )}
                    
                    {image.exifData.shutterSpeed && (
                      <div className="text-center">
                        <Timer className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                        <p className="text-xs text-gray-400">Shutter</p>
                        <p className="text-sm font-semibold text-gray-900">{image.exifData.shutterSpeed}</p>
                      </div>
                    )}
                    
                    {image.exifData.iso && (
                      <div className="text-center">
                        <Hash className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                        <p className="text-xs text-gray-400">ISO</p>
                        <p className="text-sm font-semibold text-gray-900">{image.exifData.iso}</p>
                      </div>
                    )}
                  </div>

                  {image.exifData.focalLength && (
                    <div className="pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                        <span>Focal Length:</span>
                        <span className="font-semibold text-gray-900">{image.exifData.focalLength}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tags */}
            {image.tags && image.tags.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {image.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {isZoomed && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center overflow-hidden p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Zoomed image"
          onClick={() => setIsZoomed(false)}
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 z-10"
            onClick={() => setIsZoomed(false)}
            aria-label="Close zoomed image"
          >
            <span className="text-2xl">×</span>
          </button>
          {zoomImageLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loading size="lg" />
            </div>
          )}
          <img
            src={imageUrl}
            alt={image.filename}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
            onLoad={() => setZoomImageLoading(false)}
            onLoadStart={() => setZoomImageLoading(true)}
          />
        </div>
      )}
    </Layout>
  )
}